import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.boundHandleFieldChange = this.boundHandleFieldChange || this.handleFieldChange.bind(this)
    this.registerListeners()
    this.refresh()
  }

  registerListeners() {
    this.listeningElements = new Set()

    this.triggerFieldNames.forEach((fieldName) => {
      this.fieldElements(fieldName).forEach((element) => {
        element.addEventListener("change", this.boundHandleFieldChange)
        this.listeningElements.add(element)
      })
    })
  }

  disconnect() {
    if (!this.boundHandleFieldChange || !this.listeningElements) return

    this.listeningElements.forEach((element) => {
      element.removeEventListener("change", this.boundHandleFieldChange)
    })
    this.listeningElements.clear()
  }

  handleFieldChange() {
    this.refresh()
  }

  refresh() {
    const triggerMap = this.triggersForElement()
    if (!triggerMap) return

    const shouldShow = this.matchesTriggerMap(triggerMap)
    this.element.style.display = shouldShow ? "block" : "none"
  }

  triggersForElement() {
    const triggerValue = this.element.dataset.conditionalFieldsTriggerValue
    if (!triggerValue) return null

    try {
      const parsed = JSON.parse(triggerValue)
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null
    } catch (error) {
      return null
    }
  }

  matchesTriggerMap(triggerMap) {
    if (!triggerMap || Object.keys(triggerMap).length === 0) return false

    return Object.entries(triggerMap).every(([fieldName, expected]) => {
      const currentValues = this.currentValuesForField(fieldName).map((value) => String(value))
      if (currentValues.length === 0) return false

      const expectedValues = Array.isArray(expected) ? expected : [expected]
      return expectedValues.map((value) => String(value)).some((value) => currentValues.includes(value))
    })
  }

  currentValuesForField(fieldName) {
    const values = []

    this.fieldElements(fieldName).forEach((element) => {
      if (element.tagName === "SELECT") {
        if (element.multiple) {
          values.push(...Array.from(element.selectedOptions).map((option) => option.value))
        } else if (element.value !== "") {
          values.push(element.value)
        }
        return
      }

      if (element.type === "checkbox" || element.type === "radio") {
        if (element.checked) values.push(element.value)
        return
      }

      if (element.value !== "") values.push(element.value)
    })

    return values
  }

  fieldElements(fieldName) {
    const scope = this.scopeElement
    return Array.from(
      scope.querySelectorAll(
        `select[name="${fieldName}"], input[name="${fieldName}"], input[name="${fieldName}[]"]`
      )
    )
  }

  get scopeElement() {
    return this.element.closest("form") || document
  }

  get triggerFieldNames() {
    const names = new Set()
    const triggerMap = this.triggersForElement()
    if (!triggerMap) return []

    Object.keys(triggerMap).forEach((fieldName) => names.add(fieldName))

    return Array.from(names)
  }
}
